import { useState } from "react";
import * as Plot from "@observablehq/plot";
import PlotFigure from "../../PlotFigure.js";
import { WifiIcon } from '../common/Icons';
import ChartOverlay from '../common/ChartOverlay';

function DevicePlot({ wifiData }) {
    const [expandedPlot, setExpandedPlot] = useState(null);

    if (!wifiData || wifiData.records_found === 0) {
        return (
            <div className="no-data-message">
                <p>No data found for selected interval.</p>
            </div>
        );
    }

    // Transform 2.4GHz data for plotting
    const ibss = wifiData.signal_24?.ibss?.map(d => ({
        bssid: d.bssid,
        t: new Date(d.t),
        rssi: parseFloat(d.rssi)
    })) || [];

    const obss = wifiData.signal_24?.obss?.map(d => ({
        bssid: d.bssid,
        t: new Date(d.t),
        rssi: parseFloat(d.rssi)
    })) || [];

    const snr = wifiData.signal_24?.snr?.map(d => ({
        t: new Date(d.t),
        snr: parseFloat(d.snr)
    })) || [];

    const noise = wifiData.signal_24?.noise?.map(d => ({
        t: new Date(d.t),
        noise: parseFloat(d.noise)
    })) || [];

    // Transform 5GHz data for plotting
    const ibss_50 = wifiData.signal_50?.ibss?.map(d => ({
        bssid: d.bssid,
        t: new Date(d.t),
        rssi: parseFloat(d.rssi)
    })) || [];

    const obss_50 = wifiData.signal_50?.obss?.map(d => ({
        bssid: d.bssid,
        t: new Date(d.t),
        rssi: parseFloat(d.rssi)
    })) || [];

    const snr_50 = wifiData.signal_50?.snr?.map(d => ({
        t: new Date(d.t),
        snr: parseFloat(d.snr)
    })) || [];

    const noise_50 = wifiData.signal_50?.noise?.map(d => ({
        t: new Date(d.t),
        noise: parseFloat(d.noise)
    })) || [];

    const createPlot = (data, title, yLabel, strokeColor = null, strokeField = null, colorRange = null) => {
        if (!data.length) {
            return null;
        }

        const valueKey = strokeField ? 'rssi' : data[0]?.snr !== undefined ? 'snr' : 'noise';
        const plotOptions = {
            color: strokeField
                ? {
                      legend: true,
                      type: 'ordinal',
                      range: colorRange || ['#74f8ff', '#9ea7ff', '#f7a9ff', '#7df2c9', '#ffe46b', '#8efbff'],
                  }
                : undefined,
            y: {
                grid: true,
                label: yLabel,
            },
            x: {
                type: 'time',
                grid: true,
            },
            marks: [
                Plot.ruleY([0]),
                Plot.line(data, {
                    x: 't',
                    y: valueKey,
                    stroke: strokeColor || strokeField,
                }),
                Plot.tip(
                    data,
                    Plot.pointerX({
                        x: 't',
                        y: valueKey,
                        title: (d) => {
                            if (strokeField) {
                                return `📶 BSSID: ${d.bssid}\nRSSI: ${d.rssi} dBm\nTime: ${d.t.toLocaleString()}`;
                            }
                            if (d.snr !== undefined) {
                                return `📶 SNR: ${d.snr} dB\nTime: ${d.t.toLocaleString()}`;
                            }
                            return `📶 Noise: ${d.noise} dBm\nTime: ${d.t.toLocaleString()}`;
                        },
                        pointer: 'mouse',
                        className: 'plot-tip neon-plot-tip',
                        textPadding: 12,
                        pointerSize: 14,
                        lineHeight: 1.6,
                        fontSize: 12,
                        fontWeight: '600',
                    })
                ),
            ],
        };

        const handleExpand = () => setExpandedPlot({ title, yLabel, plotOptions });

        return (
            <div className="plot-section" key={title}>
                <header className="plot-section__header plot-section__header--compact">
                    <h3>
                        <WifiIcon size="24px" />
                        {title}
                    </h3>
                    <button type="button" className="plot-expand-button" onClick={handleExpand}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={plotOptions} />
            </div>
        );
    };

    const plotIbss24 = createPlot(ibss, "2.4GHz IBSS RSSI", "Mesh AP Signal Strength (dBm)", null, "bssid");
    const plotIbss50 = createPlot(
        ibss_50,
        "5GHz IBSS RSSI",
        "Mesh AP Signal Strength (dBm)",
        null,
        "bssid",
        ['#68f6ff', '#9cc9ff', '#ffbffc', '#7af8d6', '#ffe8a6', '#b8f4ff']
    );
    const plotObss24 = createPlot(obss, "2.4GHz OBSS RSSI", "OBSS AP Signal Strength (dBm)", null, "bssid");
    const plotObss50 = createPlot(obss_50, "5GHz OBSS RSSI", "OBSS AP Signal Strength (dBm)", null, "bssid");
    const plotNoise24 = createPlot(noise, "2.4GHz Noise", "Noise (dBm)", "red");
    const plotNoise50 = createPlot(noise_50, "5GHz Noise", "Noise (dBm)", "red");
    const plotSnr24 = createPlot(snr, "2.4GHz Signal to Noise", "Signal to Noise Ratio (dB)", "green");
    const plotSnr50 = createPlot(snr_50, "5GHz Signal to Noise", "Signal to Noise Ratio (dB)", "green");

    return (
        <div className="plots-container">
            {/* IBSS RSSI Plots - Side by Side */}
            {(plotIbss24 || plotIbss50) && (
                <div className="plot-row">
                    {plotIbss24 ? <div className="plot-column">{plotIbss24}</div> : null}
                    {plotIbss50 ? <div className="plot-column">{plotIbss50}</div> : null}
                </div>
            )}

            {/* OBSS RSSI Plots - Side by Side */}
            {(plotObss24 || plotObss50) && (
                <div className="plot-row">
                    {plotObss24 ? <div className="plot-column">{plotObss24}</div> : null}
                    {plotObss50 ? <div className="plot-column">{plotObss50}</div> : null}
                </div>
            )}

            {/* Noise Plots - Side by Side */}
            {(plotNoise24 || plotNoise50) && (
                <div className="plot-row">
                    {plotNoise24 ? <div className="plot-column">{plotNoise24}</div> : null}
                    {plotNoise50 ? <div className="plot-column">{plotNoise50}</div> : null}
                </div>
            )}

            {/* SNR Plots - Side by Side */}
            {(plotSnr24 || plotSnr50) && (
                <div className="plot-row">
                    {plotSnr24 ? <div className="plot-column">{plotSnr24}</div> : null}
                    {plotSnr50 ? <div className="plot-column">{plotSnr50}</div> : null}
                </div>
            )}

            {expandedPlot ? (
                <ChartOverlay
                    title={expandedPlot.title}
                    description={`Y-axis: ${expandedPlot.yLabel}`}
                    onClose={() => setExpandedPlot(null)}
                >
                    <PlotFigure options={expandedPlot.plotOptions} height="min(72vh, 680px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}
        </div>
    );
}

export default DevicePlot;